import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, FileText, ChevronDown, ChevronUp, Loader2, Info, ListChecks, Hash, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../Shared/PageHeader';
import { StatCard } from '../ui';

const ImportConfirmation = ({ importData, onContinue, onBack, onBackToDashboard }) => {
  const [assessmentName, setAssessmentName] = useState(importData?.name || 'Untitled Assessment');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [showItemsList, setShowItemsList] = useState(false);
  
  // Use items passed through from import response
  const importedItems = importData?.imported_items || [];
  
  const handleSaveName = async (newName) => {
    const trimmedName = newName.trim();
    
    if (!trimmedName) {
      setNameError('Assessment name cannot be empty');
      setAssessmentName(importData.name || 'Untitled Assessment');
      return;
    }
    
    // Don't save if name hasn't changed
    if (trimmedName === importData.name) {
      return;
    }
    
    setIsSavingName(true);
    setNameError('');
    
    try {
      await api.patch(`/api/assessment-builder/assessments/${importData.assessment_uuid}/`, { name: trimmedName });
      // Update the importData name
      importData.name = trimmedName;
    } catch (error) {
      console.error('Error updating assessment name:', error);
      const errorMsg = error.response?.data?.error || 'Failed to update name';
      setNameError(errorMsg);
      // Revert to original name on error
      setAssessmentName(importData.name || 'Untitled Assessment');
    } finally {
      setIsSavingName(false);
    }
  };
  // Use data from import response directly - no need to fetch again
  if (!importData?.assessment_id) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-500/30 dark:bg-red-500/10">
          <h2 className="mb-1 text-base font-semibold text-red-800 dark:text-red-200">No Assessment Data</h2>
          <p className="mb-4 text-[13.5px] text-red-700 dark:text-red-300">Import data is missing</p>
          <button
            onClick={onBack}
            className="btn btn-secondary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const getLibraryTypeName = (type) => {
    const types = {
      'vpg': 'VPG Global Library',
      'sponsor': 'Sponsor Library',
      'study': 'Study-Specific Library'
    };
    return types[type] || type;
  };

  return (
    <div>
      {/* Success Header */}
      <PageHeader
        title="Assessment Imported Successfully!"
        subtitle={importData.message || `Successfully imported "${importData.name}" with ${importData.items_imported} items`}
      />

      <div className="space-y-6">
        {importData.items_reused > 0 && (
          <div className="flex items-start gap-2.5 rounded-lg border border-gray-200/70 bg-gray-50 p-3.5 text-[12.5px] leading-relaxed text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <p>
              {importData.items_created} new questions added to Item Bank, {importData.items_reused} reused from existing library
            </p>
          </div>
        )}

        {/* Key numbers */}
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard
            label="Items Imported"
            icon={ListChecks}
            value={(
              <button
                onClick={() => setShowItemsList(!showItemsList)}
                className="inline-flex items-center gap-2 rounded-md text-gray-900 transition-colors hover:text-primary-700 dark:text-gray-50 dark:hover:text-primary-400"
              >
                <span>{importData.items_imported || 0}<span className="text-sm font-medium tracking-normal text-gray-500 dark:text-gray-400"> items</span></span>
                {showItemsList ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </button>
            )}
          />
          <StatCard label="Assessment ID" icon={Hash} value={`#${importData.assessment_id}`} />
          <StatCard label="Survey ID" icon={Hash} value={`#${importData.survey_id}`} />
        </div>

        <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 space-y-5">
            {/* Assessment Overview */}
            <section className="card p-5">
              <h2 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Assessment Overview</h2>
              <div className="mt-4">
                <label className="label">Assessment Name</label>
                <div className="relative">
                  <input
                    type="text"
                    value={assessmentName}
                    onChange={(e) => {
                      setAssessmentName(e.target.value);
                      setNameError('');
                    }}
                    onBlur={(e) => handleSaveName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    disabled={isSavingName}
                    className={`input pr-10 ${nameError ? 'input-error' : ''}`}
                    placeholder="Enter assessment name"
                  />
                  {isSavingName && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
                    </div>
                  )}
                </div>
                {nameError && (
                  <p className="field-error">{nameError}</p>
                )}
              </div>
              <dl className="mt-5 divide-y divide-gray-100 border-t border-gray-100 text-[13px] dark:divide-white/[0.06] dark:border-white/[0.06]">
                <div className="flex items-center justify-between gap-4 py-2.5">
                  <dt className="text-gray-500 dark:text-gray-400">Library Location</dt>
                  <dd className="text-right font-medium text-gray-900 dark:text-gray-50">{getLibraryTypeName(importData.library_type)}</dd>
                </div>
                <div className="flex items-center justify-between gap-4 py-2.5">
                  <dt className="text-gray-500 dark:text-gray-400">Status</dt>
                  <dd>
                    <span className="badge badge-dot badge-warning">Draft</span>
                  </dd>
                </div>
              </dl>
            </section>

            {/* Imported Items List */}
            {showItemsList && (
              <section className="card overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Imported Items</h3>
                    <span className="badge badge-gray tabular-nums">{importData.items_imported}</span>
                  </div>
                </div>
                {importedItems.length > 0 ? (
                  <div className="max-h-96 divide-y divide-gray-100 overflow-y-auto border-t border-gray-100 dark:divide-white/[0.06] dark:border-white/[0.06]">
                    {importedItems.map((item, index) => (
                      <div
                        key={item.id || index}
                        className="flex items-start gap-4 px-5 py-3.5"
                      >
                        <span className="w-6 flex-shrink-0 text-right text-[13px] font-medium tabular-nums text-gray-400 dark:text-gray-500">
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
                            {item.item_text || item.text || item.question_text || 'Untitled Question'}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            {item.question_type && (
                              <span className="badge badge-gray">
                                {item.question_type}
                              </span>
                            )}
                            {item.response_type && (
                              <span className="badge badge-gray">
                                {item.response_type}
                              </span>
                            )}
                            {item.category && (
                              <span className="badge badge-gray">
                                {item.category}
                              </span>
                            )}
                            {item.domain && (
                              <span className="badge badge-gray">
                                {item.domain}
                              </span>
                            )}
                            {item.source && (
                              <span className="badge badge-gray">
                                {item.source}
                              </span>
                            )}
                            {item.validated && (
                              <span className="badge badge-dot badge-success">
                                Validated
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-t border-gray-100 px-5 py-10 text-center dark:border-white/[0.06]">
                    <FileText className="mx-auto h-6 w-6 text-gray-400 dark:text-gray-500" />
                    <p className="mt-2 text-[13.5px] text-gray-500 dark:text-gray-400">No items to display</p>
                  </div>
                )}
              </section>
            )}

            {/* Validation Warnings */}
            {importData.validation_warnings && importData.validation_warnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-[12.5px] leading-relaxed text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                <h3 className="flex items-center gap-2 text-[13px] font-semibold">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  Import Warnings
                </h3>
                <ul className="mt-2 list-disc space-y-1 pl-10">
                  {importData.validation_warnings.map((warning, index) => (
                    <li key={index}>
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Next Steps Info */}
          <aside className="card p-5">
            <h3 className="flex items-center gap-2 text-[15px] font-semibold text-gray-900 dark:text-gray-50">
              <FileText className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              What's Next?
            </h3>
            <p className="mt-2 text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
              Your assessment has been imported successfully with {importData.items_imported} items. Next steps:
            </p>
            <ul className="mt-3 space-y-2 text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
              <li className="flex items-start">
                <span className="mr-2 text-gray-400 dark:text-gray-500">•</span>
                <span>Continue to Screen Builder to design the assessment layout</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-gray-400 dark:text-gray-500">•</span>
                <span>Configure item properties and response validation</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-gray-400 dark:text-gray-500">•</span>
                <span>Add conditional logic and skip patterns if needed</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-gray-400 dark:text-gray-500">•</span>
                <span>Preview and test before publishing</span>
              </li>
            </ul>
          </aside>
        </div>

        {/* Navigation */}
        <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200/80 bg-white px-1 py-4 dark:border-white/[0.08] dark:bg-gray-900">
          <button
            onClick={onBackToDashboard || onBack}
            className="btn btn-secondary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>

          <button
            onClick={() => onContinue(importData)}
            className="btn btn-primary"
          >
            Continue to Screen Builder
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportConfirmation;
